/**
 * Page Visibility bridge for the ws layer: how long has this tab been hidden,
 * and a promise that settles once it is on screen again. Standard approach for
 * "don't keep a socket alive for a tab nobody is looking at": the browser
 * already tells us (`visibilitychange`); we only have to listen.
 */

let hiddenSince: number | undefined =
  typeof document !== 'undefined' && document.visibilityState === 'hidden' ? Date.now() : undefined
const visibleListeners = new Set<() => void>()

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      hiddenSince ??= Date.now()
      return
    }

    hiddenSince = undefined
    const listeners = [...visibleListeners]
    visibleListeners.clear()
    listeners.forEach((listener) => listener())
  })
}

export function isPageVisible(): boolean {
  return hiddenSince === undefined
}

/** Milliseconds this tab has been continuously hidden; 0 while visible. */
export function getHiddenDuration(): number {
  return hiddenSince === undefined ? 0 : Date.now() - hiddenSince
}

/** Resolves immediately when visible, otherwise on the next `visibilitychange` to visible. */
export function whenPageVisible(): Promise<void> {
  if (isPageVisible()) {
    return Promise.resolve()
  }

  return new Promise((resolve) => {
    visibleListeners.add(resolve)
  })
}
